import { curSym } from "@/lib/company";
import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import { fmtTL } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PackageX, Search, Loader2, Trash2, Trophy, AlertTriangle, Camera, Upload, X } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";

const REASONS = ["Son Kullanma (SKT)", "Hasar / Bozulma", "Kırılma / Dökülme", "Çalıntı / Kayıp", "Diğer"];

export default function WasteTab() {
  const { can } = useAuth();
  const canEnter = can("stock.adjust");
  const canManage = can("role.manage");
  const { activeLocId, locations: locs, isGlobal } = useActiveLocation();
  const needLoc = isGlobal || (locs?.length || 0) > 1;
  const [locId, setLocId] = useState("");
  useEffect(() => { if (activeLocId) setLocId(activeLocId); }, [activeLocId]);
  const [list, setList] = useState([]);
  const [scores, setScores] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState("");
  const [docNo, setDocNo] = useState("");
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);
  const [alertCfg, setAlertCfg] = useState({ enabled: false, threshold: 0 });
  const [savingAlert, setSavingAlert] = useState(false);
  const wastePaged = usePaged(list, 8);

  const load = () => {
    api.get("/waste").then((r) => setList(r.data)).catch(() => {});
    api.get("/manager-scores").then((r) => setScores(r.data.scores || [])).catch(() => {});
    api.get("/waste/alert-config").then((r) => setAlertCfg(r.data)).catch(() => {});
  };
  const saveAlert = async () => {
    setSavingAlert(true);
    try {
      await api.put("/waste/alert-config", { enabled: alertCfg.enabled, threshold: Number(alertCfg.threshold) || 0 });
      toast.success("Fire uyarı ayarı kaydedildi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setSavingAlert(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/products?q=${encodeURIComponent(q)}&page_size=6`).then((r) => setResults(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  // Ürün-ara açılır listesini dışarı tıklayınca / Esc ile kapat (takılı kalma düzeltmesi)
  useEffect(() => {
    const onDown = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setResults([]); };
    const onEsc = (e) => { if (e.key === "Escape") setResults([]); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, []);

  const loadTop = () => api.get("/products?page_size=5").then((r) => setResults(r.data.items || [])).catch(() => {});

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhoto(data); toast.success("Fotoğraf yüklendi"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Yükleme başarısız"); }
    finally { setUploading(false); }
  };

  const genDocNo = async (silent = false) => {
    try {
      const { data } = await api.get("/logistics/generate-doc-no?type=waste");
      setDocNo(data.doc_no);
      if (!silent) toast.success(`Belge No üretildi: ${data.doc_no}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!sel) return toast.error("Ürün seçin");
    if (!qty || Number(qty) <= 0) return toast.error("Geçerli miktar girin");
    if (note.trim().length < 5) return toast.error("Açıklama zorunlu (en az 5 karakter)");
    if (!docNo) return toast.error("Belge No zorunlu — 'Üret'e basın");
    if (!photo?.url) return toast.error("Fire fotoğrafı zorunludur");
    if (needLoc && !locId) return toast.error("Lokasyon (depo/mağaza) seçin");
    setSaving(true);
    try {
      const res = await api.post("/waste", { product_id: sel.id, quantity: Number(qty), reason, note: note.trim(), photo_url: photo.url, doc_no: docNo || undefined, location_id: needLoc ? locId : null });
      if (res.data?.pending_approval) toast.info(res.data.message || "Fire kaydı muhasebe onayına gönderildi");
      else toast.success("Fire kaydedildi");
      setSel(null); setQ(""); setQty(""); setNote(""); setDocNo(""); setPhoto(null); setResults([]);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="acc-waste-tab">
      <p className="text-sm text-slate-500">Fire/zayiat kayıtları depo/mağaza yetkilisince oluşturulur, <b>muhasebe onayına</b> düşer; onaylanınca stoktan düşülür ve <b>sorumlunun puanı</b> azalır. Açıklama zorunludur.</p>
      {canManage && (
        <div className="flex flex-wrap items-center gap-3 rounded-md bg-amber-50 p-3 ring-1 ring-inset ring-amber-200" data-testid="waste-alert-config">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Otomatik Fire Uyarısı</span>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" data-testid="waste-alert-enabled" checked={!!alertCfg.enabled} onChange={(e) => setAlertCfg({ ...alertCfg, enabled: e.target.checked })} className="h-3.5 w-3.5 accent-amber-600" />
            Aktif
          </label>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Aylık eşik {curSym()}</span>
            <Input data-testid="waste-alert-threshold" value={alertCfg.threshold ?? ""} onChange={(e) => setAlertCfg({ ...alertCfg, threshold: e.target.value.replace(/[^0-9.]/g, "") })} className="h-8 w-28 text-right tabular" />
          </div>
          <Button data-testid="waste-alert-save" onClick={saveAlert} disabled={savingAlert} size="sm" className="bg-amber-600 text-white hover:bg-amber-700">
            {savingAlert ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Kaydet
          </Button>
          <span className="text-[11px] text-slate-500">Bir depo bu ayki fire maliyeti eşiği aşınca yönetici/muhasebeye anlık uyarı (+SMS) gider.</span>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {canEnter && (
          <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="acc-waste-form">
            <div className="mb-3 flex items-center gap-2"><PackageX className="h-4 w-4 text-red-500" /><p className="font-heading text-sm font-bold text-slate-800">Fire / Zayiat Girişi</p></div>
            <div className="space-y-3">
              {needLoc && (
                <div>
                  <Label>Lokasyon (Depo/Mağaza) *</Label>
                  <select data-testid="acc-waste-location" value={locId} onChange={(e) => setLocId(e.target.value)}
                    className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                    <option value="">Seçin</option>
                    {(locs || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              )}
              <div className="relative" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="acc-waste-search" value={sel ? sel.name : q} onFocus={() => { if (!sel && q.length < 2) loadTop(); }} onChange={(e) => { setSel(null); setQ(e.target.value); }} placeholder="Ürün ara... (tıklayınca ilk 5 ürün gelir)" className="pl-9" />
                {!sel && results.length > 0 && (
                  <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                    {results.map((p) => (
                      <button key={p.id} data-testid={`acc-waste-pick-${p.id}`} onClick={() => { setSel(p); setResults([]); setQ(""); if (!docNo) genDocNo(true); }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50">
                        <span>{p.name} <span className="font-mono text-[10px] text-slate-400">{p.sku}</span></span>
                        <span className="text-[11px] text-slate-400">stok {p.stock_on_hand} {p.base_unit}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Miktar *</Label><Input data-testid="acc-waste-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 tabular" /></div>
                <div><Label>Neden</Label>
                  <select data-testid="acc-waste-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                    className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Açıklama * <span className="text-xs font-normal text-slate-400">(en az 5 karakter)</span></Label>
                <Input data-testid="acc-waste-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="Örn. soğuk zincir kırıldı, 3 koli çürüdü" />
              </div>
              <div>
                <Label>Belge No * <span className="text-xs font-normal text-slate-400">(otomatik üretilir, taslağa yazın)</span></Label>
                <div className="mt-1 flex items-center gap-2">
                  <div data-testid="acc-waste-doc-no" className="flex h-10 flex-1 items-center rounded-md border border-input bg-slate-50 px-3 font-mono text-[15px] font-bold tracking-wider text-slate-800">
                    {docNo || <span className="font-sans text-sm font-normal text-slate-400">Üret'e basın →</span>}
                  </div>
                  <Button type="button" variant="outline" size="sm" data-testid="acc-waste-doc-gen" onClick={() => genDocNo(false)} className="h-10 shrink-0 px-3 text-xs">
                    {docNo ? "Yenile" : "Üret"}
                  </Button>
                </div>
              </div>
              <div>
                <Label>Fotoğraf * <span className="text-xs font-normal text-slate-400">(muhasebe onayı için zorunlu)</span></Label>
                <input id="waste-photo-file" type="file" accept="image/*" capture="environment" className="hidden" data-testid="acc-waste-photo-input" onChange={uploadPhoto} />
                {photo?.url ? (
                  <div className="mt-1 flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 text-sm ring-1 ring-inset ring-emerald-200" data-testid="acc-waste-photo-ok">
                    <span className="flex items-center gap-1.5 text-emerald-700"><Camera className="h-4 w-4" /> {photo.filename || "Fotoğraf eklendi"}</span>
                    <button onClick={() => setPhoto(null)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" disabled={uploading} onClick={() => document.getElementById("waste-photo-file").click()}
                    data-testid="acc-waste-photo-btn" className="mt-1 w-full border-dashed border-slate-300 text-slate-600">
                    {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Fotoğraf Yükle / Çek
                  </Button>
                )}
              </div>
              <Button data-testid="acc-waste-submit" onClick={submit} disabled={saving} className="w-full bg-red-600 text-white hover:bg-red-700">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Fire Kaydet
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="acc-waste-scores">
          <div className="mb-3 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /><p className="font-heading text-sm font-bold text-slate-800">Sorumlu Puan Tablosu (90g)</p></div>
          <div className="space-y-1.5">
            {scores.length === 0 && <p className="text-sm text-slate-400">Puan cezası kaydı yok 👍</p>}
            {scores.map((s) => (
              <div key={s.user_email} data-testid={`score-row-${s.user_email}`} className="flex items-center justify-between rounded-sm bg-slate-50 px-3 py-2">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-slate-700">{s.user_email}</p><p className="text-[10px] text-slate-400">{s.location_name || "—"} · {s.events} ceza</p></div>
                <span className={`tabular text-sm font-bold ${s.score >= 80 ? "text-emerald-600" : s.score >= 50 ? "text-amber-600" : "text-red-600"}`}>{s.score}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="acc-waste-list">
          <div className="mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" /><p className="font-heading text-sm font-bold text-slate-800">Son Fire Kayıtları</p></div>
          <div className="space-y-1.5">
            {list.length === 0 && <p className="text-sm text-slate-400">Kayıt yok.</p>}
            {wastePaged.slice.map((w) => (
              <div key={w.id} data-testid={`waste-row-${w.id}`} className="rounded-sm bg-slate-50 px-3 py-2 text-sm">
                <div className="flex items-center justify-between"><span className="font-medium text-slate-700">{w.product_name}</span><span className="tabular text-red-600">{w.quantity}{w.cost != null ? ` · ${fmtTL(w.cost)}` : ""}</span></div>
                <p className="text-[10px] text-slate-400">{w.reason} · {w.location_name || "—"} · {w.responsible}{w.doc_no ? ` · ` : ""}{w.doc_no && <span className="font-mono text-slate-500">{w.doc_no}</span>}</p>
                {w.note && <p className="text-[11px] text-slate-500">“{w.note}”</p>}
              </div>
            ))}
          </div>
          {list.length > 0 && (
            <Pagination page={wastePaged.page} pageSize={wastePaged.pageSize} total={wastePaged.total}
              onPage={wastePaged.setPage} onPageSize={wastePaged.setPageSize} sizes={[8, 20, 50]} testid="waste-pagination" />
          )}
        </div>
      </div>
    </div>
  );
}
